import { defineStore } from "pinia";
import {
  ReadersStore,
  CreateReader,
  UpdateReader,
  FavReader,
  Reader,
} from "@/terminals/types/readers";
import { useSnackBarStore } from "@/shared/stores/snackbar";
import axios from "@/app/plugins/axios";
import i18n from "@/app/plugins/i18n";
import { getErrorMessage } from "@/shared/utils/errorMessage";

const toastCaughtError = (error: unknown): void => {
  useSnackBarStore().toastError({ message: getErrorMessage(error) });
};

const patchReaderInCollection = (
  readers: Reader[] | null,
  updatedReader: Reader,
): Reader[] | null => {
  if (!Array.isArray(readers)) {
    return readers;
  }

  const readerIndex = readers.findIndex((reader) => reader.id === updatedReader.id);
  if (readerIndex < 0) {
    return readers;
  }

  const nextReaders = [...readers];
  nextReaders.splice(readerIndex, 1, {
    ...nextReaders[readerIndex],
    ...updatedReader,
  });
  return nextReaders;
};

const resolveReaderUpdateResponse = (
  responseData: unknown,
  terminalId: string,
  payload: UpdateReader,
  readers: Reader[] | null,
  selectedReader: Reader | null,
): Reader | null => {
  if (
    responseData &&
    typeof responseData === "object" &&
    typeof (responseData as Reader).id === "string"
  ) {
    return responseData as Reader;
  }

  const existingReader =
    readers?.find((reader) => reader.id === terminalId) ||
    (selectedReader?.id === terminalId ? selectedReader : null);

  if (!existingReader) {
    return null;
  }

  const nextReceiptPrinterId = Object.prototype.hasOwnProperty.call(payload, "receiptPrinterId")
    ? (payload.receiptPrinterId ?? null)
    : (existingReader.receiptPrinterId ?? null);

  return {
    ...existingReader,
    ...payload,
    ...(Object.prototype.hasOwnProperty.call(payload, "receiptPrinterId")
      ? {
          receiptPrinterId: nextReceiptPrinterId,
          receiptPrinter: !nextReceiptPrinterId
            ? null
            : existingReader.receiptPrinter?.id === nextReceiptPrinterId
              ? existingReader.receiptPrinter
              : null,
        }
      : {}),
  } as Reader;
};

export const useReaderStore = defineStore("reader", {
  state: (): ReadersStore => ({
    locationReaders: [],
    selectedReader: null,
    isLoading: false,
  }),
  getters: {
    getSelectedReader: (state) => state.selectedReader,
  },
  actions: {
    setSelectedReader(reader: Reader | null): void {
      this.selectedReader = reader;
    },

    async createReader(payload: CreateReader): Promise<Reader | undefined> {
      try {
        const response = await axios.post<Reader>("/terminal", payload);
        return response.data;
      } catch (error) {
        toastCaughtError(error);
        return undefined;
      }
    },

    async updateReader(terminalId: string, payload: UpdateReader): Promise<Reader | null> {
      const response = await axios.put(`/terminal/${terminalId}`, payload);
      const updatedReader = resolveReaderUpdateResponse(
        response.data,
        terminalId,
        payload,
        this.locationReaders,
        this.selectedReader,
      );

      if (!updatedReader) {
        return null;
      }

      this.locationReaders = patchReaderInCollection(this.locationReaders, updatedReader);

      if (
        this.selectedReader &&
        typeof this.selectedReader === "object" &&
        this.selectedReader.id === updatedReader.id
      ) {
        this.selectedReader = {
          ...this.selectedReader,
          ...updatedReader,
        };
      }

      return updatedReader;
    },

    async addFavoriteReader(payload: FavReader): Promise<void> {
      try {
        await axios.post("/terminal/favorite", payload);
        useSnackBarStore().toastSuccess({
          message: i18n.global.t("page.terminals.addedFav"),
        });
      } catch (error) {
        toastCaughtError(error);
      }
    },

    async removeFavoriteReader(payload: FavReader): Promise<void> {
      try {
        await axios.post("/terminal/delete/favorite", payload);
        useSnackBarStore().toastSuccess({
          message: i18n.global.t("page.terminals.removedFav"),
        });
      } catch (error) {
        const errors = i18n.global.t("page.terminals.error");
        const axiosError = error as { code?: string; data?: { error?: string } };
        const errorCode = axiosError?.code;
        const translated =
          errorCode && typeof errors === "object" && errors !== null
            ? (errors as Record<string, string>)[errorCode]
            : undefined;
        const errorMessage = translated || axiosError?.data?.error || getErrorMessage(error);
        useSnackBarStore().toastError({ message: errorMessage });
      }
    },

    async deleteReader(readerId: string, firebaseId: string): Promise<void> {
      try {
        await axios.delete(`/terminal/${readerId}/${firebaseId}`);
      } catch (error) {
        toastCaughtError(error);
      }
    },

    async fetchFavReader(): Promise<unknown> {
      try {
        this.isLoading = true;
        const response = await axios.get("/terminal/favorite");
        return response.data;
      } catch (error) {
        toastCaughtError(error);
        return undefined;
      } finally {
        this.isLoading = false;
      }
    },

    async retrieveFirestoreTerminal(terminalId: string | undefined): Promise<unknown> {
      try {
        this.isLoading = true;
        const response = await axios.get(`/terminal/firestoreTerminal/${terminalId}`);
        return response.data;
      } catch (error) {
        toastCaughtError(error);
        return undefined;
      } finally {
        this.isLoading = false;
      }
    },
    async listReaders(
      active?: boolean,
      deleted?: boolean,
      locationId?: string,
    ): Promise<Reader[] | undefined> {
      try {
        this.isLoading = true;
        let url = "/terminal";
        const params = new URLSearchParams();

        if (active !== undefined) {
          params.append("active", active.toString());
        }
        if (deleted !== undefined) {
          params.append("deleted", deleted.toString());
        }
        if (locationId !== undefined) {
          params.append("locationId", locationId);
        }
        if (params.toString()) {
          url += "?" + params.toString();
        }
        const response = await axios.get<Reader[]>(url);
        return response.data;
      } catch (error) {
        toastCaughtError(error);
        return undefined;
      } finally {
        this.isLoading = false;
      }
    },

    async setLocationReaders(
      active?: boolean,
      deleted?: boolean,
      locationId?: string,
    ): Promise<void> {
      try {
        this.isLoading = true;
        let url = "/terminal";
        const params = new URLSearchParams();

        if (active !== undefined) {
          params.append("active", active.toString());
        }
        if (deleted !== undefined) {
          params.append("deleted", deleted.toString());
        }
        if (locationId !== undefined) {
          params.append("locationId", locationId);
        }
        if (params.toString()) {
          url += "?" + params.toString();
        }
        const response = await axios.get<Reader[]>(url);
        this.locationReaders = response.data;
      } catch (error) {
        toastCaughtError(error);
      } finally {
        this.isLoading = false;
      }
    },

    async cancelTerminalAction(terminalId: string | undefined): Promise<void> {
      try {
        this.isLoading = true;
        if (terminalId === undefined) return;
        await axios.post(`/terminal/${terminalId}/cancel`);
      } catch (error) {
        toastCaughtError(error);
      } finally {
        this.isLoading = false;
      }
    },

    async activateReader(terminalId: string): Promise<void> {
      try {
        this.isLoading = true;
        await axios.post(`/terminal/activate/${terminalId}`);
      } catch (error) {
        toastCaughtError(error);
      } finally {
        this.isLoading = false;
      }
    },

    async deactivateReader(terminalId: string): Promise<void> {
      try {
        this.isLoading = true;
        await axios.post(`/terminal/deactivate/${terminalId}`);
      } catch (error) {
        toastCaughtError(error);
      } finally {
        this.isLoading = false;
      }
    },

    async simulatePayment(terminalId: string, payload: unknown): Promise<void> {
      try {
        this.isLoading = true;
        await axios.post(`/terminal/simulate/${terminalId}`, payload);
      } catch (error) {
        toastCaughtError(error);
      } finally {
        this.isLoading = false;
      }
    },
  },
});
